import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useLocation } from "react-router-dom";
import SchoolInnerPageBanner from "../../components/SchoolInnerPageBanner";
import QuestionBank from "../../components/AcademicQuestionBank";
import TechnoAEP from "../../components/AcademicTechnoAEP";
import useScrollAnimation from "../../constants/useScrollAnimation";
import "./AcademicsPage.css";
const baseAPIUrl = process.env.REACT_APP_API_BASE_URL;

// Styles are loaded from AcademicsPage.css

const AcademicsPage = () => {
  const location = useLocation();
  const { school_slug } = useParams();

  const [loading, setLoading] = useState(true);
  const [preprimary, setPreprimary] = useState(null);
  const [primary, setPrimary] = useState(null);
  const [senior, setSenior] = useState({
    section: null,
    blocks: []
  });

  const [secondary, setSecondary] = useState(null);
  const [card, setStreams] = useState([]);
  const [card_grid_1_content, setCardGridContent1] = useState([]);
  const [card_grid_1_block, setCardGridBlock1] = useState([]);
  const [card_grid_2_content, setCardGridContent2] = useState([]);
  const [card_grid_2_block, setCardGridBlock2] = useState([]);
  const [card_grid_3_content, setCardGridContent3] = useState([]);
  const [card_grid_3_block, setCardGridBlock3] = useState([]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const sectionId = location.state?.scrollTo;
    if (!sectionId) return;

    const timer = setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [location.key]);

  useEffect(() => {
    scrollToTop();
  }, []);

  /* ---------------- FETCH PRE-PRIMARY ---------------- */
  useEffect(() => {
    const finalSchoolSlug = school_slug || "tigps-main";
    setLoading(true);
    axios
      .get(
        `${baseAPIUrl}api/academic/pre_primary/${finalSchoolSlug}`
      )
      .then(res => {
        if (res.data.status === 1) setPreprimary(res.data.data);
        else setPreprimary(null);
      })
      .catch(() => setPreprimary(null))
      .finally(() => setLoading(false));
  }, [school_slug]);

  /* ---------------- FETCH PRIMARY ---------------- */
  useEffect(() => {
    const finalSchoolSlug = school_slug || "tigps-main";

    axios
      .get(
        `${baseAPIUrl}api/academic/primary/${finalSchoolSlug}`
      )
      .then(res => {
        if (res.data.status === 1) setPrimary(res.data.data);
        else setPrimary(null);
      })
      .catch(() => setPrimary(null));
  }, [school_slug]);

  /* ---------------- FETCH SENIOR ---------------- */
  useEffect(() => {
    const finalSchoolSlug = school_slug || "tigps-main";
    axios
      .get(
        `${baseAPIUrl}api/academic/senior/${finalSchoolSlug}`
      )
      .then(res => {
        console.log("SENIOR API RESPONSE:", res.data);
        if (res.data.status === 1) {
          setSenior(res.data.data);
        }
      })
      .catch(err => console.error(err));
  }, [school_slug]);

  /* ---------------- FETCH SENIOR SECONDARY---------------- */
  useEffect(() => {
    const finalSchoolSlug = school_slug || "tigps-main";
    fetchSeniorSecondary();
  }, [school_slug]);

  const fetchSeniorSecondary = async () => {
    const finalSchoolSlug = school_slug || "tigps-main";
    setLoading(true);

    try {
      const res = await axios.get(
        `${baseAPIUrl}api/academic/senior_secondary/${finalSchoolSlug}`
      );

      if (res.data?.status === 1 && res.data?.data) {
        setSecondary(res.data.data.section || null);
        setCardGridContent1(res.data.data.card_grid_1_content || null);
        setCardGridBlock1(res.data.data.card_grid_1_block || []);
        setCardGridContent2(res.data.data.card_grid_2_content || null);
        setCardGridBlock2(res.data.data.card_grid_2_block || []);
        setCardGridContent3(res.data.data.card_grid_3_content || null);
        setCardGridBlock3(res.data.data.card_grid_3_block || []);
      } else {
        resetSeniorSecondary();
      }
    } catch (err) {
      console.error("Senior Secondary API Error:", err);
      resetSeniorSecondary();
    } finally {
      setLoading(false);
    }
  };

  const resetSeniorSecondary = () => {
    setSecondary(null);
    setCardGridContent1(null);
    setCardGridBlock1([]);
    setCardGridContent2(null);
    setCardGridBlock2([]);
    setCardGridContent3(null);
    setCardGridBlock3([]);
  };



  const [prePrimaryHeaderRef, isPrePrimaryHeaderVisible] = useScrollAnimation(0.2);
  const [prePrimaryContentRef, isPrePrimaryContentVisible] = useScrollAnimation(0.2);

  const [primaryHeaderRef, isPrimaryHeaderVisible] = useScrollAnimation(0.2);
  const [primaryContentRef, isPrimaryContentVisible] = useScrollAnimation(0.2);

  const [seniorHeaderRef, isSeniorHeaderVisible] = useScrollAnimation(0.2);
  const [seniorContentRef, isSeniorContentVisible] = useScrollAnimation(0.2);

  const [seniorSecondaryHeaderRef, isSeniorSecondaryHeaderVisible] = useScrollAnimation(0.2);
  const [seniorSecondaryContentRef, isSeniorSecondaryContentVisible] = useScrollAnimation(0.2);

  return (
    <div className="academics-page">
      <SchoolInnerPageBanner />

      {/* Pre-Primary Program Section */}
      {!loading && preprimary?.section && (
        <div id="pre-primary-program" className="academics-page-pre-primary-section">
          <div className="academics-page-container">
            <div ref={prePrimaryHeaderRef} className={`section-header ${isPrePrimaryHeaderVisible ? "animate-in" : ""}`}>
              <h2 className="academics-page-pre-primary-title section-title-hm">
                {preprimary.section.heading}
              </h2>
            </div>

            <div ref={prePrimaryContentRef} className={`schools-right ${isPrePrimaryContentVisible ? "animate-in" : ""}`}>
              <p className="academics-page-pre-primary-intro">{preprimary.section.introduction_text}</p>

              {preprimary.blocks.map((block, idx) => (
                <div key={idx} className="academics-page-block-wrapper">

                  {block.heading && <h3 className="academics-page-block-heading">{block.heading}</h3>}

                  {/* BULLET LIST */}
                  {block.block_type === "bullet_list" && (
                    <ul className="academics-page-bullet-list">
                      {block.items.map((item, i) => (
                        <li className="academics-page-bullet-item" key={i}>{item}</li>
                      ))}
                    </ul>
                  )}

                  {/* CARD GRID */}
                  {block.block_type === "card_grid" && (

                    <div className="row" >
                      {block.items.map((card, i) => (
                        <div key={i} className="col-md-3 mb-3" >

                          <div className="card p-3 text-center academics-page-card-grid-item">{card}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* PARAGRAPH */}
                  {block.block_type === "paragraph" && (
                    <p>{block.content}</p>
                  )}

                </div>
              ))}

              <p className="academics-page-pre-primary-highlight">{preprimary.section.highlight_text}</p>

              {preprimary.section.section_image && (
                <div className="academics-page-image-wrapper">
                  <img
                    src={preprimary.section.section_image}
                    alt={preprimary.section.heading}
                    className="academics-page-section-image"
                  />
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* Primary Program Section */}
      {!loading && primary?.section && (
        <div id="primary-program" className="academics-page-primary-section">
          <div className="academics-page-container">
            <div ref={primaryHeaderRef} className={`section-header ${isPrimaryHeaderVisible ? "animate-in" : ""}`}>
              {/* SECTION HEADING */}
              <h2 className="academics-page-primary-title section-title-hm">
                {primary.section.heading}
              </h2>
            </div>

            <div ref={primaryContentRef} className={`schools-right ${isPrimaryContentVisible ? "animate-in" : ""}`}>
              {/* INTRO */}
              <p className="academics-page-primary-intro">
                {primary.section.introduction_text}
              </p>

              {/* BLOCKS */}
              {primary.blocks.map((block, idx) => {
                let content = {};

                try {
                  content = typeof block.content === "string"
                    ? JSON.parse(block.content)
                    : block.content;
                } catch {
                  content = {};
                }

                /* ---------- CARD GRID (SUBJECTS + FEATURES) ---------- */
                if (block.block_type === "card_grid") {
                  return (
                    <div key={idx}>

                      <h3 className="academics-page-primary-block-heading">
                        {block.heading}
                      </h3>

                      {/* INTRO 1 */}
                      <p className="academics-page-paragraph-margin">
                        {content.intro_1}
                      </p>

                      {/* SUBJECT CARDS */}
                      <div className="row mb-4">
                        {content.items_1?.map((item, i) => (
                          <div key={i} className="col-md-6 col-lg-3 mb-3">
                            <div className="academics-page-primary-subject-card">
                              {item}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* INTRO 2 */}
                      <p className="academics-page-paragraph-margin">
                        {content.intro_2}
                      </p>

                      {/* FEATURE CARDS */}
                      <div className="row mb-4">
                        {content.items_2?.map((card, i) => (
                          <div key={i} className="col-md-4 mb-3">
                            <div className="academics-page-primary-feature-card">
                              <h4 className="academics-page-primary-feature-title">
                                {card.heading}
                              </h4>
                              <p className="feature-cards-p">
                                {card.content}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                    </div>
                  );
                }

                /* ---------- SIMPLE CARD GRID ---------- */
                if (block.block_type === "card_grid_simple") {
                  return (
                    <div key={idx}>
                      <h3 className="academics-page-primary-simple-heading">
                        {block.heading}
                      </h3>

                      <p className="academics-page-paragraph-margin">
                        {content.intro}
                      </p>

                      <div className="row">
                        {content.items?.map((item, i) => (
                          <div key={i} className="col-md-6 col-lg-3 mb-3">
                            <div className="academics-page-primary-simple-card">
                              {item}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                return null;
              })}

              {/* IMAGE */}
              {primary.section.section_image && (
                <div className="academics-page-image-wrapper">
                  <img
                    src={primary.section.section_image}
                    alt={primary.section.heading}
                    className="academics-page-section-image"
                  />
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* Senior Section */}
      {senior?.section && (
        <div id="senior-section" className="academics-page-senior-section">
          <div className="academics-page-container">
            <div ref={seniorHeaderRef} className={`section-header ${isSeniorHeaderVisible ? "animate-in" : ""}`}>
              <h2 className="text-center mb-5 academics-page-senior-title section-title-hm">
                {senior.section.heading}
              </h2>
            </div>

            <div ref={seniorContentRef} className={`schools-right ${isSeniorContentVisible ? "animate-in" : ""}`}>
              <p className="mb-4 academics-page-senior-intro">
                {senior.section.introduction_text}
              </p>

              {(senior.blocks || []).map((block, idx) => (
                <div key={idx} className="mb-5">
                  <h3 className="text-center mb-4 academics-page-senior-block-title">{block.heading}</h3>

                  <div className="row academics-page-senior-row">
                    {(block.items || []).map((item, i) => (
                      <div key={i} className="col-6 col-md-3 mb-3">
                        <div className="academic-card academics-page-senior-card">{item}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="academic-highlight academics-page-senior-highlight">
                {senior.section.highlight_text}
              </div>

              {senior.section.section_image && (
                <div className="text-center mt-5">
                  <img
                    src={senior.section.section_image}
                    alt="Senior"
                    className="academics-page-senior-image"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Senior Secondary Program Section */}
      {secondary && (
        <div id="senior-secondary-program" className="senior-secondary-section">
          <div className="senior-secondary-container">
            <div ref={seniorSecondaryHeaderRef} className={`section-header ${isSeniorSecondaryHeaderVisible ? "animate-in" : ""}`}>
              {/* Title */}
              <h2 className="senior-secondary-title section-title-hm">
                {secondary.heading}
              </h2>
            </div>

            <div ref={seniorSecondaryContentRef} className={`schools-right ${isSeniorSecondaryContentVisible ? "animate-in" : ""}`}>
              {/* Introduction */}
              {(secondary.introduction_text) && (
                <p className="primary-text">
                  {secondary.introduction_text}
                </p>
              )}

              {/* card_grid_1 */}
              {card_grid_1_block?.length > 0 && (
                <>
                  <h3 className="section-heading">
                    {card_grid_1_content.card_heading}
                  </h3>
                  <p className="academics-page-secondary-grid1-intro">
                    {card_grid_1_content.card_intro}
                  </p>

                  <div className="row mb-5">
                    {card_grid_1_block.map((stream, index) => (
                      <div key={index} className="col-md-4 mb-4">
                        <div className="stream-card">
                          <h4>{stream.heading}</h4>
                          <p>{stream.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* card_grid_2 */}
              {card_grid_2_block?.length > 0 && (
                <>
                  <h3 className="section-heading">
                    {card_grid_2_content.card_heading}
                  </h3>
                  <p className="academics-page-secondary-grid2-intro">
                    {card_grid_2_content.card_intro}
                  </p>
                  <div className="row mb-5">
                    {card_grid_2_block.map((item, index) => (
                      <div key={index} className="col-md-6 col-lg-3 mb-3">
                        <div className="support-card">
                          <h4>{typeof item === "string" ? item : item.content}</h4>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* card_grid_3 */}
              {card_grid_3_block?.length > 0 && (
                <>
                  <h3 className="section-heading">
                    {card_grid_3_content.card_heading}
                  </h3>
                  <p className="academics-page-secondary-grid3-intro">
                    {card_grid_3_content.card_intro}
                  </p>
                  <div className="row">
                    {card_grid_3_block.map((item, index) => (
                      <div key={index} className="col-md-6 col-lg-3 mb-3">
                        <div className="holistic-card">
                          <h4>{item.heading}</h4>
                          <p>{item.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* IMAGE */}
              {secondary.image && (
                <div className="secondary-image-wrapper">
                  <img
                    src={secondary.image}
                    alt="Senior Secondary Education"
                  />
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* Question Bank Section */}
      <QuestionBank />

      {/* Techno AEP Section */}
      {/* <TechnoAEP /> */}
    </div>
  );
};

export default AcademicsPage;